import { message } from "ant-design-vue"
import { getAuth } from "firebase/auth"
import { defineStore } from "pinia"
import { FirebaseTokenLoader, JobsService } from "@telarya/telarya-sdk"
import { toRaw, watch } from "vue"

import { useCompanyDriversStore } from "@/components/Companies/companyDriversStore"
import { useVehiclesStore } from "@/components/Vehicles/VehiclesStore"
import generateEndpoint from "@/endpoint"
import { createAuthenticatedHttpClient } from "@/services/httpClient"
import reportError from "@/utils/reportError"

export const useVehicleAssignmentsStore = defineStore("VehicleAssignmentsStore", {
  state: () => ({
    assignments: [],
    isInitialized: false,
    isFetching: false,
    fetchPromise: null,
    tenantId: null,
    pusher: null,
    pusherChannelName: null,
    pusherChannel: null,
    sdkService: null,
    driversWatchStopper: null
  }),
  actions: {
    resetState() {
      this.teardownPusher()
      if (typeof this.driversWatchStopper === "function") {
        this.driversWatchStopper()
      }
      this.assignments = []
      this.isInitialized = false
      this.isFetching = false
      this.fetchPromise = null
      this.tenantId = null
      this.pusher = null
      this.pusherChannelName = null
      this.pusherChannel = null
      this.sdkService = null
      this.driversWatchStopper = null
    },
    initializeSdkService() {
      if (this.sdkService) {
        return
      }
      this.sdkService = new JobsService(
        generateEndpoint(),
        createAuthenticatedHttpClient(new FirebaseTokenLoader(getAuth()))
      )
    },
    initialize(tenant, webSocket) {
      if (!tenant || !tenant.id) {
        throw new Error("VehicleAssignmentsStore: Invalid tenant provided during initialization.")
      }
      if (!webSocket) {
        throw new Error("VehicleAssignmentsStore: Invalid webSocket service provided during initialization.")
      }
      const tenantChanged = this.tenantId && this.tenantId !== tenant.id
      if (tenantChanged) {
        this.resetState()
      } else if (this.isInitialized) {
        return
      }

      this.tenantId = tenant.id
      this.pusher = toRaw(webSocket)
      this.initializeSdkService()
      this.subscribeToPusher()
      this.fetchAssignments()

      // Re-apply assignments when drivers list changes
      const driversStore = useCompanyDriversStore()
      this.driversWatchStopper = watch(
        () => driversStore.records.map((d) => d?.id),
        () => this.applyAssignmentsToDrivers()
      )

      this.isInitialized = true
    },
    async fetchAssignments() {
      if (this.isFetching) {
        return this.fetchPromise
      }
      this.isFetching = true
      this.fetchPromise = (async () => {
        try {
          const activeTenantId = this.tenantId
          this.initializeSdkService()
          const assignments = await this.sdkService.getUserAndVehicleAssignments()
          if (this.tenantId !== activeTenantId) {
            this.logToConsole(
              `Discarded vehicle assignments response for stale tenant ${activeTenantId}; current tenant ${this.tenantId}`,
              "info"
            )
            return
          }
          this.setAssignments(assignments)
        } catch (error) {
          reportError(error, { context: "VehicleAssignmentsStore.fetchAssignments", tenantId: this.tenantId })
          this.notifyUser("Error fetching vehicle assignments. " + error.message, "error")
          console.error("VehicleAssignmentsStore", error)
        } finally {
          this.isFetching = false
          this.fetchPromise = null
        }
      })()
      return this.fetchPromise
    },
    setAssignments(assignments) {
      this.assignments = assignments || []
      this.applyAssignmentsToDrivers()
    },
    upsertAssignments(newAssignments) {
      if (!Array.isArray(newAssignments) || newAssignments.length === 0) return

      const byId = new Map(this.assignments.filter((a) => a?.id).map((a) => [a.id, a]))
      const withoutId = this.assignments.filter((a) => !a?.id)

      newAssignments.forEach((assignment) => {
        if (assignment?.id) {
          byId.set(assignment.id, assignment)
        } else {
          withoutId.push(assignment)
        }
      })

      this.assignments = [...byId.values(), ...withoutId]
      this.applyAssignmentsToDrivers()
    },
    removeAssignments(assignmentsToRemove) {
      if (!Array.isArray(assignmentsToRemove) || assignmentsToRemove.length === 0) return

      const ids = new Set(assignmentsToRemove.map((a) => a?.id).filter(Boolean))
      this.assignments = this.assignments.filter((a) => !a?.id || !ids.has(a.id))
      this.applyAssignmentsToDrivers()
    },
    subscribeToPusher() {
      if (!this.tenantId) {
        throw new Error("VehicleAssignmentsStore: Tenant ID is required for Pusher subscription.")
      }
      if (!this.pusher) {
        throw new Error("VehicleAssignmentsStore: No webSocket service.")
      }

      const channelName = `${this.tenantId}_VehicleAssignments`
      this.pusherChannelName = channelName
      this.pusherChannel = this.pusher.subscribe(channelName)

      this.pusherChannel.bind("Update", (data) => {
        this.logToConsole(`Pusher message received on channel ${channelName} (Update):`, "info")
        this.upsertAssignments(data?.assignments || [])
      })

      this.pusherChannel.bind("Delete", (data) => {
        this.logToConsole(`Pusher message received on channel ${channelName} (Delete):`, "info")
        this.removeAssignments(data?.assignments || [])
      })

      this.pusherChannel.bind("pusher:subscription_succeeded", () => {
        this.logToConsole(`Subscribed to Pusher channel: ${channelName}`, "info")
      })

      this.pusherChannel.bind("pusher:subscription_error", (error) => {
        this.logToConsole(`Error subscribing to Pusher channel ${channelName}: ${error}`, "error")
      })
    },
    teardownPusher() {
      if (this.pusherChannel) {
        this.pusherChannel.unbind_all()
      }
      if (this.pusher && this.pusherChannelName) {
        this.pusher.unsubscribe(this.pusherChannelName)
      }
      this.pusherChannelName = null
      this.pusherChannel = null
    },
    applyAssignmentsToDrivers() {
      const driversStore = useCompanyDriversStore()
      if (!driversStore?.isInitialized) return

      const vehiclesStore = useVehiclesStore()
      const assignmentsByUser = new Map()
      this.assignments.forEach((assignment) => {
        if (!assignment?.userId) return

        const vehicles = assignmentsByUser.get(assignment.userId) || []
        const vehicle =
          assignment.vehicle ||
          (vehiclesStore?.isInitialized ? vehiclesStore.getById(assignment.vehicleId) : null) ||
          null
        if (vehicle) {
          vehicles.push(vehicle)
        }
        assignmentsByUser.set(assignment.userId, vehicles)
      })

      driversStore.records.forEach((driver) => {
        if (!driver) return
        driver.assignedVehicles = assignmentsByUser.get(driver.id) || []
      })
    },
    notifyUser(msg, type = "info") {
      message.error(`VehicleAssignmentsStore [${type.toUpperCase()}] ${msg}`)
    },
    logToConsole(msg, type = "info") {
      console.log("VehicleAssignmentsStore " + `[${type.toUpperCase()}] ${msg}`)
    }
  }
})
